import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "../lib/api";
import { usePageEvent, track, captureAttribution } from "../lib/tracker";
import {
  ArrowRight, Sparkles, FileSearch, Target, MessagesSquare, Trophy, ShieldCheck,
  CheckCircle2, Plus, Minus, Rocket, Flame, BarChart3,
  Route, Building2, LineChart, Loader2
} from "lucide-react";

const features = [
  { icon: FileSearch, title: "Resume Analyzer", desc: "ATS, formatting, keywords & skill relevance — broken down into actionable scores." },
  { icon: Target, title: "Skill Gap Map", desc: "Compare your profile to the target role and get a personalized learning roadmap." },
  { icon: MessagesSquare, title: "AI Mock Interviews", desc: "L1 HR + L2 technical rounds generated from your resume and target role." },
  { icon: ShieldCheck, title: "Recruiter Readiness", desc: "A single 0-100 score that signals when you're truly Offer Ready." },
  { icon: Trophy, title: "Gamification", desc: "Streaks, badges and leaderboards to keep you sharp every week." },
  { icon: BarChart3, title: "Progress Charts", desc: "Track resume score, skill growth, and interview performance over time." },
];

const highlights = [
  {
    icon: Route,
    title: "Personalized Learning Roadmaps",
    desc: "Recommends targeted courses, projects, and learning paths based on assessment results.",
    grad: "from-indigo-500 to-sky-500",
  },
  {
    icon: Building2,
    title: "Industry-Aligned Skill Mapping",
    desc: "Benchmarks learners against the skills required for in-demand job roles.",
    grad: "from-sky-500 to-cyan-500",
  },
  {
    icon: LineChart,
    title: "Progress & Readiness Tracking",
    desc: "Monitors skill development and provides insights into job readiness over time.",
    grad: "from-emerald-500 to-teal-500",
  },
];

const PLAN_FEATURES = {
  free: ["1 Resume Analysis", "1 L1 Mock Interview", "Basic Feedback"],
  weekly: ["7-day full access", "Unlimited Resume Analysis", "Daily Mock Interviews", "ATS + Skill Gap reports", "Email support"],
  monthly: ["30-day access", "Unlimited Resume Analysis", "Unlimited Mock Interviews", "ATS + Skill Gap reports", "Weekly progress reports", "Priority processing"],
  quarterly: ["90-day access", "Everything in Monthly", "Personalised career roadmap", "Premium ATS templates", "Recruiter readiness tracking", "Save 17% vs monthly"],
  yearly: ["365-day access", "Everything in Quarterly", "Full premium templates", "Year-long career roadmap", "Highest priority queue", "Save 48% vs monthly"],
};

const PLAN_ORDER = ["free", "weekly", "monthly", "quarterly", "yearly"];

const steps = [
  { n: "01", title: "Upload your resume", body: "PDF or DOCX. We parse skills, education, projects, and keywords in seconds." },
  { n: "02", title: "Get AI insights", body: "Six subscores, missing keywords, and tailored suggestions for your target role." },
  { n: "03", title: "Practice and improve", body: "Take role-specific L1 and L2 mock interviews and review every answer." },
  { n: "04", title: "Hit Offer Ready", body: "Watch your Recruiter Readiness climb until you're ready to apply with confidence." },
];

const testimonials = [];

const faqs = [
  { q: "Is SkillGap Pilot free to try?", a: "Yes. The free plan includes 1 resume analysis and 1 L1 mock interview so you can see results before upgrading." },
  { q: "Which file formats are supported?", a: "PDF, DOC, and DOCX up to 5MB. We extract text locally before sending it to the AI." },
  { q: "Which AI powers the analysis?", a: "We use GPT-5.4 to produce structured ATS, skill, and interview evaluations." },
  { q: "Can I cancel anytime?", a: "Yes. Monthly and annual subscriptions can be canceled in one click from your profile." },
];

export default function Landing() {
  usePageEvent("landing_view");
  // Capture first-touch attribution (UTMs + referrer) on first page view.
  // Idempotent: runs once per browser, first-touch wins.
  if (typeof window !== "undefined") captureAttribution();
  const [open, setOpen] = useState(0);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/payments/plans");
        if (!mounted) return;
        setPlans(data.items || []);
      } catch (e) { /* ignore — landing degrades gracefully */ }
      if (mounted) setPlansLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  const planMap = Object.fromEntries(plans.map(p => [p.id, p]));

  return (
    <div className="min-h-screen bg-aurora text-zinc-900">
      {/* NAV */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="h-8 w-8 bg-gradient-to-br from-indigo-500 via-sky-500 to-emerald-500 text-white rounded-md flex items-center justify-center">
              <Sparkles size={16} />
            </div>
            <span className="font-display font-black text-lg">SkillGap <span className="rainbow-text">Pilot</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-700">
            <a href="#features" className="hover:text-black">Features</a>
            <a href="#how" className="hover:text-black">How it Works</a>
            <a href="#pricing" className="hover:text-black">Pricing</a>
            <a href="#faq" className="hover:text-black">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login" className="text-sm font-medium px-3 py-2 rounded-md hover:bg-zinc-100">Login</Link>
            <Link to="/signup" data-testid="nav-signup" onClick={() => track("cta_click", { source: "nav" })} className="text-sm font-semibold px-4 py-2 rounded-md btn-gradient hover:opacity-90">
              Start Free
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-200">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="tag mb-6 glow-pill"><span className="h-1.5 w-1.5 rounded-full bg-sky-500 pulse-dot" /> Analyze. Improve. Get Hired.</div>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-balance">
                Know Your <span className="rainbow-text">Skill Gap</span><br />
                Before Recruiters Do.
              </h1>
              <div className="mt-8">
                <Link
                  to="/signup"
                  data-testid="hero-free-assessment-cta"
                  className="group relative inline-flex items-center gap-3 px-7 py-4 rounded-lg btn-gradient font-display font-bold text-lg tracking-tight text-white shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40 hover:-translate-y-0.5 transition-all"
                >
                  <span className="absolute -top-2 -right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-black uppercase tracking-wider shadow-md">
                    Free
                  </span>
                  Start Your Free Assessment
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <div className="text-xs text-zinc-500 mt-2 ml-1">No credit card required · 60-second setup</div>
              </div>
              <p className="mt-6 text-lg text-zinc-600 max-w-xl leading-relaxed">
                Upload your resume, receive AI-powered insights, sharpen your interview skills,
                and stop guessing whether you&apos;re ready to apply.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/signup" data-testid="hero-cta-analyze" className="inline-flex items-center gap-2 px-5 py-3 rounded-md btn-gradient font-semibold hover:opacity-90 transition">
                  Analyze Resume <ArrowRight size={16} />
                </Link>
                <Link to="/signup" data-testid="hero-cta-trial" className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-zinc-300 font-semibold hover:border-zinc-900 transition">
                  Start Free Trial
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 fade-up" style={{ animationDelay: '120ms' }}>
              <div className="card-flat p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Recruiter Readiness</div>
                    <div className="font-display font-black text-5xl mt-2">84<span className="text-zinc-400 text-2xl">/100</span></div>
                    <div className="tag tag-success mt-3"><CheckCircle2 size={12} /> Competitive</div>
                  </div>
                  <div className="h-24 w-24 rounded-full" style={{ background: `conic-gradient(#6366f1 28%, #0ea5e9 28% 56%, #10b981 56% 84%, #F4F4F5 84%)` }}>
                    <div className="h-full w-full rounded-full bg-white scale-[0.78] flex items-center justify-center font-mono text-sm">
                      84
                    </div>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-3 gap-3 text-xs">
                  <div className="border border-zinc-200 rounded-md p-3">
                    <div className="text-zinc-500">Resume</div>
                    <div className="font-mono font-semibold text-lg mt-1">88</div>
                  </div>
                  <div className="border border-zinc-200 rounded-md p-3">
                    <div className="text-zinc-500">ATS</div>
                    <div className="font-mono font-semibold text-lg mt-1">92</div>
                  </div>
                  <div className="border border-zinc-200 rounded-md p-3">
                    <div className="text-zinc-500">Skill</div>
                    <div className="font-mono font-semibold text-lg mt-1">81</div>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-zinc-200 flex items-center gap-2 text-xs text-zinc-600">
                  <Flame size={14} className="text-orange-500" /> 12-day streak — keep going!
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="card-flat p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Interview Score</div>
                  <div className="font-mono font-semibold text-2xl mt-1">79<span className="text-zinc-400 text-sm">/100</span></div>
                </div>
                <div className="card-flat p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Skill Match</div>
                  <div className="font-mono font-semibold text-2xl mt-1">81<span className="text-zinc-400 text-sm">%</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS — 3 feature cards (replaces previous statistics block) */}
      <section className="border-b border-zinc-200" data-testid="highlights-section">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <div className="tag">Why SkillGap Pilot</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-4">
              Built to turn skill gaps into <span className="rainbow-text">offers</span>.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {highlights.map((h) => (
              <div
                key={h.title}
                className="card-flat p-6 fade-up"
                data-testid={`highlight-${h.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className={`h-10 w-10 rounded-md bg-gradient-to-br ${h.grad} text-white flex items-center justify-center`}>
                  <h.icon size={18} />
                </div>
                <div className="font-display font-bold text-lg mt-4">{h.title}</div>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <div className="tag">Features</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-4">
              Everything you need between<br/>resume and recruiter.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, idx) => {
              const grads = [
                'from-indigo-500 to-sky-500',
                'from-sky-500 to-cyan-500',
                'from-cyan-500 to-emerald-500',
                'from-emerald-500 to-teal-500',
                'from-amber-400 to-orange-400',
                'from-violet-500 to-indigo-500',
              ];
              return (
                <div key={f.title} className="card-flat p-6">
                  <div className={`h-9 w-9 rounded-md bg-gradient-to-br ${grads[idx % grads.length]} text-white flex items-center justify-center`}>
                    <f.icon size={16} />
                  </div>
                  <div className="font-display font-bold text-lg mt-4">{f.title}</div>
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="tag">How it works</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-4">Four steps to Offer Ready.</h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((s) => (
              <div key={s.n} className="card-flat p-6">
                <div className="font-mono text-zinc-400 text-xs">STEP {s.n}</div>
                <div className="font-display font-bold text-xl mt-3">{s.title}</div>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS removed per request */}

      {/* PRICING — same 5-tier grid as authenticated /app/pricing */}
      <section id="pricing" className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="tag">Pricing</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-4">Pick the plan that fits your <span className="rainbow-text">hiring season</span>.</h2>
              <p className="text-zinc-600 mt-3 text-sm max-w-2xl">Pay via UPI, Google Pay, PhonePe, Paytm, cards, net banking or wallets. All plans renew manually — cancel any time by letting it lapse.</p>
            </div>
            <Link to="/pricing" className="text-sm font-semibold inline-flex items-center gap-1 hover:underline" data-testid="see-all-pricing">
              See full pricing page <ArrowRight size={14} />
            </Link>
          </div>

          {plansLoading ? (
            <div className="mt-10 flex items-center gap-2 text-zinc-500 text-sm" data-testid="plans-loading">
              <Loader2 size={14} className="animate-spin" /> Loading plans…
            </div>
          ) : (
            <div className="mt-10 grid md:grid-cols-3 lg:grid-cols-5 gap-4">
              {PLAN_ORDER.map((planId) => (
                <LandingPlanCard
                  key={planId}
                  planId={planId}
                  plan={planMap[planId]}
                  features={PLAN_FEATURES[planId] || []}
                />
              ))}
            </div>
          )}

          <div className="mt-6 text-xs text-zinc-500">
            Subscription dates, payment history & invoices are available on your <Link to="/login" className="text-indigo-600 font-semibold">Dashboard</Link> after signing in.
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <div className="tag">FAQ</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-4">Questions, answered.</h2>
          <div className="mt-10 border-t border-zinc-200">
            {faqs.map((f, i) => (
              <div key={f.q} className="border-b border-zinc-200">
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="w-full flex items-center justify-between py-5 text-left"
                >
                  <span className="font-display font-semibold text-lg">{f.q}</span>
                  {open === i ? <Minus size={16} /> : <Plus size={16} />}
                </button>
                {open === i && (
                  <p className="pb-5 text-zinc-600 leading-relaxed">{f.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-indigo-600 via-sky-600 to-emerald-500 text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <div className="tag mx-auto" style={{ background: 'rgba(255,255,255,.12)', color: '#fff', borderColor: 'rgba(255,255,255,.18)' }}>
            <Rocket size={12} /> Get Offer Ready in 21 days
          </div>
          <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tighter mt-6 text-balance">
            Stop applying blind.<br/>Start applying ready.
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/signup" data-testid="footer-cta-signup" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white text-zinc-900 font-semibold hover:bg-zinc-100 transition">
              Start Free <ArrowRight size={16} />
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-white/30 font-semibold hover:bg-white/10 transition">
              See pricing
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-6 text-sm text-zinc-500">
          <div>
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 bg-gradient-to-br from-indigo-500 to-sky-500 text-white rounded-md flex items-center justify-center"><Sparkles size={14} /></div>
              <span className="font-display font-bold text-black">SkillGap Pilot</span>
            </div>
            <div className="mt-2 text-xs text-zinc-500">AI-powered career intelligence for the next wave of job seekers.</div>
            <div className="mt-3 text-xs">© {new Date().getFullYear()} SkillGap Pilot · India · All rights reserved.</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-700">Product</div>
            <ul className="mt-2 space-y-1.5">
              <li><a href="#features" className="hover:text-zinc-900">Features</a></li>
              <li><a href="#pricing" className="hover:text-zinc-900">Pricing</a></li>
              <li><a href="#faq" className="hover:text-zinc-900">FAQ</a></li>
              <li><Link to="/pricing" className="hover:text-zinc-900">All plans</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-700">Support</div>
            <ul className="mt-2 space-y-1.5">
              <li>
                <a href="mailto:support@skillgappilot.com" className="hover:text-zinc-900" data-testid="footer-support-email">
                  support@skillgappilot.com
                </a>
              </li>
              <li>
                <a href="https://skillgappilot.com" target="_blank" rel="noreferrer" className="hover:text-zinc-900" data-testid="footer-site-url">
                  skillgappilot.com
                </a>
              </li>
              <li><Link to="/login" className="hover:text-zinc-900">Login</Link></li>
              <li><Link to="/signup" className="hover:text-zinc-900">Create account</Link></li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}

function LandingPlanCard({ planId, plan, features }) {
  const isFree = planId === "free";
  const highlight = planId === "quarterly";
  const priceLabel = isFree ? "₹0" : (plan ? `₹${Number(plan.amount).toLocaleString("en-IN")}` : "—");
  const durationLabel = isFree
    ? "Forever free"
    : plan
      ? (plan.duration_days === 7 ? "/week"
        : plan.duration_days === 30 ? "/month"
        : plan.duration_days === 90 ? "/quarter"
        : "/year")
      : "";
  const ctaLabel = isFree ? "Start Free" : "Buy Now";
  return (
    <div
      className={`relative p-6 rounded-xl border ${highlight
        ? 'bg-gradient-to-br from-indigo-600 via-sky-500 to-emerald-500 text-white border-transparent shadow-xl'
        : 'bg-white border-zinc-200'}`}
      data-testid={`landing-plan-${planId}`}
    >
      {highlight && (
        <div className="absolute -top-3 left-6 text-[10px] tracking-[0.25em] uppercase font-bold bg-black text-white px-2 py-1 rounded">
          Best value
        </div>
      )}
      <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{plan?.name || (isFree ? "Free" : planId)}</div>
      <div className="mt-3 flex items-baseline gap-1">
        <div className="font-display font-black text-4xl">{priceLabel}</div>
        <div className="text-sm opacity-70">{durationLabel}</div>
      </div>
      <ul className="mt-5 space-y-2 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <CheckCircle2 size={14} className={`mt-1 ${highlight ? 'text-white' : 'text-emerald-600'}`} />
            <span className={highlight ? 'opacity-95' : 'text-zinc-700'}>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to={isFree ? "/signup" : `/signup?plan=${planId}`}
        data-testid={`landing-plan-cta-${planId}`}
        className={`mt-6 inline-flex items-center justify-center w-full px-4 py-2.5 rounded-md font-semibold text-sm gap-1 transition ${highlight
          ? 'bg-white text-zinc-900 hover:bg-zinc-100'
          : 'bg-gradient-to-r from-indigo-500 to-sky-500 text-white hover:opacity-90'}`}
      >
        {ctaLabel} <ArrowRight size={14} />
      </Link>
      {!isFree && (
        <p className={`text-xs mt-3 ${highlight ? 'opacity-70' : 'text-zinc-500'}`}>
          Secure checkout · UPI · Cards · Net banking
        </p>
      )}
    </div>
  );
}
